/**
 * Shared AI Assists catalog — cards with enable toggles, tier sections, search when many.
 * Used on Settings → Agents and the AI Assists deep-link page.
 */

import { useMemo, useState } from 'react';
import { Sparkles, Bot, CheckCircle2, CircleSlash, Loader2, Search } from 'lucide-react';
import { getItemCountState } from '../../../components/collection/types';
import { isAiCapabilityEnabled } from '../../../lib/ai/aiAssistSettings';
import { useElevationAccess } from '../../../lib/elevationAccess';
import { assistCapabilitiesFor } from '../../../lib/ai/useAiCapability';
import {
  AI_ASSIST_TIER_SECTIONS,
  AI_EVIDENCE_LABELS,
  AI_ROLE_LABELS,
  type AiCapabilityMeta,
} from '../../../lib/ai/aiCapabilityRegistry';

export function AssistCard({
  capability,
  enabled,
  saving,
  onToggle,
}: {
  capability: AiCapabilityMeta;
  enabled: boolean;
  saving: boolean;
  onToggle: (next: boolean) => void;
}) {
  const Icon = capability.icon;

  return (
    <div className="card flex h-full flex-col gap-3">
      <div className="flex items-start gap-3">
        <div
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center  ${capability.accent.iconBg} ${capability.accent.iconText}`}
        >
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-[var(--text-pri)] m-0">{capability.name}</h3>
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium ${
                enabled ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
              }`}
            >
              {enabled ? (
                <>
                  <CheckCircle2 className="h-3 w-3" /> Enabled
                </>
              ) : (
                <>
                  <CircleSlash className="h-3 w-3" /> Disabled
                </>
              )}
            </span>
          </div>
          <p className="text-xs text-[var(--text-ter)] mt-0.5 mb-0">{capability.tagline}</p>
        </div>
      </div>

      <p className="text-sm text-[var(--text-sec)] m-0 flex-1">{capability.description}</p>

      <div className="flex flex-wrap gap-2 text-[11px] text-[var(--text-ter)]">
        <span className="px-1.5 py-0.5 rounded bg-[var(--surface)] border border-[var(--rule)]">
          {AI_ROLE_LABELS[capability.role]}
        </span>
        <span className="px-1.5 py-0.5 rounded bg-[var(--surface)] border border-[var(--rule)]">
          {AI_EVIDENCE_LABELS[capability.evidenceModel]}
        </span>
      </div>

      <label className="inline-flex items-center gap-2 text-sm text-[var(--text-sec)] cursor-pointer select-none mt-auto pt-1 border-t border-[var(--rule)]">
        <input
          type="checkbox"
          className="rounded border-[var(--rule)]"
          checked={enabled}
          disabled={saving}
          onChange={(e) => onToggle(e.target.checked)}
        />
        Allow this assist in the workspace
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--text-ter)]" />}
      </label>
    </div>
  );
}

export interface AiAssistsCatalogProps {
  workspaceId: string | null;
  savingCapabilityId: string | null;
  onToggle: (capabilityId: string, enabled: boolean) => void;
  /** When true, tier titles use Agents-page compact styling. */
  compactTier?: boolean;
}

export function AiAssistsCatalog({
  workspaceId,
  savingCapabilityId,
  onToggle,
  compactTier = false,
}: AiAssistsCatalogProps) {
  const [search, setSearch] = useState('');
  // An assist with an Elevation level (the Field reader: team) is listed only
  // for people at that level (lib/elevationAccess.ts).
  const access = useElevationAccess();
  const assists = useMemo(() => assistCapabilitiesFor(access), [access]);
  const countState = getItemCountState(assists.length);
  const showSearch = countState === 'many';

  const filteredByTier = useMemo(() => {
    const q = search.trim().toLowerCase();
    const map = new Map<string, AiCapabilityMeta[]>();
    for (const section of AI_ASSIST_TIER_SECTIONS) {
      const items = assists.filter((c) => {
        if (c.invocationTier !== section.tier) return false;
        if (!q) return true;
        return (
          c.name.toLowerCase().includes(q) ||
          c.tagline.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          AI_ROLE_LABELS[c.role].toLowerCase().includes(q)
        );
      });
      map.set(section.tier, items);
    }
    return map;
  }, [search, assists]);

  const totalVisible = AI_ASSIST_TIER_SECTIONS.reduce(
    (n, s) => n + (filteredByTier.get(s.tier)?.length ?? 0),
    0,
  );

  return (
    <div className="space-y-4">
      {showSearch && (
        <div className="relative px-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)] pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search assists…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400"
            aria-label="Search AI assists"
          />
        </div>
      )}

      {totalVisible === 0 ? (
        <p className="text-sm text-[var(--text-ter)] px-1 m-0">
          No assists match &ldquo;{search.trim()}&rdquo;.
        </p>
      ) : (
        AI_ASSIST_TIER_SECTIONS.map((section) => {
          const items = filteredByTier.get(section.tier) ?? [];
          if (items.length === 0) return null;

          return (
            <div key={section.tier} className={compactTier ? 'space-y-3' : 'mb-8 space-y-3'}>
              {compactTier ? (
                <div className="flex items-center gap-2 px-1 flex-wrap">
                  {section.tier === 'inline_assist' ? (
                    <Sparkles className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                  ) : (
                    <Bot className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                  )}
                  <p className="text-xs font-semibold text-[var(--text-sec)] m-0 uppercase tracking-wide">
                    {section.title}
                  </p>
                  <p className="text-xs text-[var(--text-ter)] m-0">— {section.description}</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    {section.tier === 'inline_assist' ? (
                      <Sparkles className="h-4 w-4 text-[var(--text-ter)]" />
                    ) : (
                      <Bot className="h-4 w-4 text-[var(--text-ter)]" />
                    )}
                    <h2 className="text-base font-semibold text-[var(--text-pri)] m-0">{section.title}</h2>
                  </div>
                  <p className="text-sm text-[var(--text-ter)] mb-0 mt-0">{section.description}</p>
                </>
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {items.map((cap) => (
                  <AssistCard
                    key={cap.id}
                    capability={cap}
                    enabled={isAiCapabilityEnabled(cap.id, workspaceId)}
                    saving={savingCapabilityId === cap.id}
                    onToggle={(next) => onToggle(cap.id, next)}
                  />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
